// Views
import PropertyView from '../views/PropertyView.vue';
import LocationView from '../views/LocationView.vue';
import FeedbackView from '../views/FeedbackView.vue';
import RoomAvailabilityView from '../views/RoomAvailabilityView.vue';
import LoginView from '../views/LoginView.vue';
import PrivacyPolicyView from '../views/PrivacyPolicyView.vue';
import TermsOfServiceView from '../views/TermsOfServiceView.vue';
import { CONFIG } from '../config';
import { SSG_ROUTE_NAMES, SSG_BUILD_ROUTES } from './ssg-build-routes.mjs';

// Admin views (lazy loaded)
const AdminDashboard = () => import('../views/admin/AdminDashboard.vue');
const AdminUsersView = () => import('../views/admin/AdminUsersView.vue');
const AdminRoomsView = () => import('../views/admin/AdminRoomsView.vue');
const AdminCmsView = () => import('../views/admin/AdminCmsView.vue');
const AdminSitePropertyView = () => import('../views/admin/AdminSitePropertyView.vue');
const AdminPoisView = () => import('../views/admin/AdminPoisView.vue');
const AdminMessagesView = () => import('../views/admin/AdminMessagesView.vue');
const AdminSettingsView = () => import('../views/admin/AdminSettingsView.vue');
const AdminProfileView = () => import('../views/admin/AdminProfileView.vue');
const AdminDocumentsView = () => import('../views/admin/AdminDocumentsView.vue');
const AdminFeedbackView = () => import('../views/admin/AdminFeedbackView.vue');
const AdminLegalView = () => import('../views/admin/AdminLegalView.vue');

const TenantDashboardLayout = () => import('../views/tenant-dashboard/TenantDashboardLayout.vue');
const TenantDashboardHomeView = () => import('../views/tenant-dashboard/TenantDashboardHomeView.vue');
const ProfileSettings = () => import('../components/profile/ProfileSettings.vue');
const TenantDashboardMessagesView = () => import('../views/tenant-dashboard/TenantDashboardMessagesView.vue');
const TenantDashboardDocumentsView = () => import('../views/tenant-dashboard/TenantDashboardDocumentsView.vue');

// Routes configuration - exported for SSG
export const routes = [
  // Dev server entry (`/`) should behave like production SPA shell:
  // redirect to locale home derived from cookie/browser/default.
  {
    path: '/',
    redirect: () => `/${CONFIG.getLocaleFromClient()}`
  },
  // SSG Public routes with language prefix
  {
    path: '/:lang',
    name: 'home',
    component: PropertyView,
    meta: { public: true, ssg: SSG_ROUTE_NAMES.has('home') },
    beforeEnter: validateLocale
  },
  {
    path: '/:lang/property',
    name: 'property',
    component: PropertyView,
    meta: { public: true, ssg: SSG_ROUTE_NAMES.has('property') },
    beforeEnter: validateLocale
  },
  {
    path: '/:lang/location',
    name: 'location',
    component: LocationView,
    meta: { public: true, ssg: SSG_ROUTE_NAMES.has('location') },
    beforeEnter: validateLocale
  },
  {
    path: '/:lang/feedback',
    name: 'feedback',
    component: FeedbackView,
    meta: { public: true, ssg: SSG_ROUTE_NAMES.has('feedback') },
    beforeEnter: validateLocale
  },
  {
    path: '/:lang/privacy-policy',
    name: 'privacy-policy',
    component: PrivacyPolicyView,
    meta: { public: true, ssg: SSG_ROUTE_NAMES.has('privacy-policy') },
    beforeEnter: validateLocale
  },
  {
    path: '/:lang/terms-of-service',
    name: 'terms-of-service',
    component: TermsOfServiceView,
    meta: { public: true, ssg: SSG_ROUTE_NAMES.has('terms-of-service') },
    beforeEnter: validateLocale
  },
  
  // SPA routes (not pre-rendered)
  {
    path: '/:lang/availability',
    name: 'availability',
    component: RoomAvailabilityView,
    meta: { public: true, spa: true, ssg: SSG_ROUTE_NAMES.has('availability') }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { public: true, hideWhenAuth: true, spa: true, layout: 'minimal' }
  },
  {
    path: '/dashboard',
    component: TenantDashboardLayout,
    meta: { requiresAuth: true, requiresTenant: true, spa: true, layout: 'tenant-dashboard' },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: TenantDashboardHomeView
      },
      {
        path: 'profile',
        name: 'tenant-dashboard-profile',
        component: ProfileSettings
      },
      {
        path: 'messages',
        name: 'tenant-dashboard-messages',
        component: TenantDashboardMessagesView
      },
      {
        path: 'documents',
        name: 'tenant-dashboard-documents',
        component: TenantDashboardDocumentsView
      }
    ]
  },

  // Admin routes (requires admin role)
  {
    path: '/admin',
    name: 'admin',
    component: AdminDashboard,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: AdminUsersView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/rooms',
    name: 'admin-rooms',
    component: AdminRoomsView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/cms',
    name: 'admin-cms',
    component: AdminCmsView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/property',
    name: 'admin-site-property',
    component: AdminSitePropertyView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/location',
    name: 'admin-pois',
    component: AdminPoisView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/messages',
    name: 'admin-messages',
    component: AdminMessagesView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/settings',
    name: 'admin-settings',
    component: AdminSettingsView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/profile',
    name: 'admin-profile',
    component: AdminProfileView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/messaging-settings',
    redirect: { path: '/admin/settings' }
  },
  {
    path: '/admin/documents',
    name: 'admin-documents',
    component: AdminDocumentsView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/legal',
    name: 'admin-legal',
    component: AdminLegalView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/feedback',
    name: 'admin-feedback',
    component: AdminFeedbackView,
    meta: { requiresAuth: true, requiresAdmin: true, spa: true, layout: 'admin' }
  },
  {
    path: '/admin/document-categories',
    redirect: { path: '/admin/documents', query: { tab: 'categories' } }
  },

  // Catch all - redirect to default locale home
  {
    path: '/:pathMatch(.*)*',
    redirect: `/${CONFIG.DEFAULT_LOCALE}`
  }
];

/**
 * Validate locale parameter in route
 * Redirects to default locale if invalid
 */
function validateLocale(to, from, next) {
  const lang = to.params.lang;
  if (CONFIG.SUPPORTED_LOCALES.includes(lang)) {
    next();
  } else {
    // Invalid locale, redirect to default
    const path = to.path.replace(`/${lang}`, `/${CONFIG.DEFAULT_LOCALE}`);
    next(path);
  }
}

/**
 * Get all SSG routes for static generation
 * Returns array of paths like ['/fr', '/fr/property', '/en', '/en/property', ...]
 */
export function getSSGPaths() {
  const paths = [];

  for (const locale of CONFIG.SUPPORTED_LOCALES) {
    for (const { path } of SSG_BUILD_ROUTES) {
      paths.push(`/${locale}${path}`);
    }
  }

  return paths;
}

/**
 * Helper to get current locale from route
 */
export function getLocaleFromRoute(route) {
  return route.params.lang || CONFIG.DEFAULT_LOCALE;
}

/**
 * Helper to build localized path
 */
export function localePath(path, locale) {
  if (path.startsWith('/')) {
    return `/${locale}${path}`;
  }
  return `/${locale}/${path}`;
}
